<template>
    <section class="settings-section" v-if="user">
        <h3 class="title-content">{{ $t('MY_ACCOUNT') }}</h3>
        <div class="row mb-5">
            <div class="col-xs-12 col-sm-4 overflow-hidden">
                <span>{{ $t('EMAIL_ADDRESS_COLON') }}</span>
                <p class="text-bold" :style="{ 'font-size': emailFontSize }">{{ user.email }}</p>
            </div>
            <div class="col-xs-12 col-sm-4">
                <span>{{ $t('PASSWORD_COLON') }}</span>
                <br />
                <button class="arrow-link" @click="showSetNewPasswordModal = true">
                    {{ $t('CHANGE_PASSWORD') }}
                    <img class="arrow-img" src="../../../assets/images/arrow-red.png" />
                </button>
            </div>
            <div class="col-xs-12 col-sm-4">
                <br />
                <button class="arrow-link" @click="showDeactivateAccountModal = true">
                    {{ $t('DEACTIVATE_ACCOUNT') }}
                    <img class="arrow-img" src="../../../assets/images/arrow-red.png" />
                </button>
            </div>
        </div>
        <div v-if="user.address && user.address.country">
            <h3 class="title-content">{{ $t('SHARING_DOCUMENTATION_TO_DOCTOR') }}</h3>
            <div class="row margin-bottom">
                <div class="col-xs-12">
                    <p>{{ $t('DOCTOR_OR_SOMEONE_ELSE_DATA') }}</p>
                </div>
                <div v-if="!showDoctorForm">
                    <div class="col-xs-12" style="width: 70%">
                        <p>{{ $t('I_DO_NOT_WANT_TO_SHARE_DOCUMENTATION') }}</p>
                    </div>
                    <button class="arrow-link" @click="showDoctorForm = true" style="padding-top: 15px">
                        {{ $t('CHANGE') }}
                        <img class="arrow-img" src="../../../assets/images/arrow-red.png" />
                    </button>
                </div>
                <form v-if="showDoctorForm" @submit.prevent="addTrustedDoctor">
                    <div class="col-sm-4">
                        <input
                            class="text-input white-input-border"
                            type="text"
                            :name="$t('NAME')"
                            :placeholder="$t('NAME')"
                            v-validate="{ required: true, regex: /^.+ .+$/ }"
                            v-model="trustedDoctor.name"
                        />
                    </div>
                    <div class="col-sm-4">
                        <input
                            class="text-input white-input-border"
                            type="email"
                            :name="$t('EMAIL_ADDRESS')"
                            :placeholder="$t('EMAIL_ADDRESS')"
                            v-validate="{ required: true, regex: emailRegex }"
                            v-model="trustedDoctor.email"
                        />
                    </div>
                    <div class="col-sm-4">
                        <button class="app-btn app-btn--big app-btn-primary">{{ $t('ADD_PERSON') }}</button>
                    </div>
                </form>
                <div v-if="trustedDoctors" class="data-details">
                    <div class="col-xs-12 mt-5">
                        <el-table :data="trustedDoctors">
                            <div slot="empty">{{ $t('EMPTY_LIST') }}</div>
                            <el-table-column type="expand">
                                <template slot-scope="scope">
                                    <p class="text-break">{{ `${$t('NAME_COLON')} ${scope.row.name}` }}</p>
                                    <p class="text-break">
                                        {{ `${$t('EMAIL_ADDRESS_COLON')} ${scope.row.email}` }}
                                    </p>
                                    <div v-if="scope.row.dateRemoved == null">
                                        <el-button
                                            @click.native.prevent="removeTrustedDoctor(scope.row)"
                                            type="text"
                                            class="app-btn app-btn--small app-btn-default"
                                            >{{ $t('DELETE') }}</el-button
                                        >
                                    </div>
                                    <div v-else>
                                        {{ $t('PERMISSION_DELETED') }}
                                        {{ dateFormat(scope.row.dateRemoved) }}
                                    </div>
                                </template>
                            </el-table-column>
                            <el-table-column prop="name" :label="$t('NAME')"></el-table-column>
                            <el-table-column prop="email" :label="$t('EMAIL_ADDRESS')"></el-table-column>
                            <el-table-column v-if="!clientWidthIsSmall">
                                <template slot-scope="scope">
                                    <div v-if="scope.row.dateRemoved == null">
                                        <el-button
                                            @click.native.prevent="removeTrustedDoctor(scope.row)"
                                            type="text"
                                            class="app-btn app-btn--small app-btn-default"
                                            >{{ $t('DELETE') }}</el-button
                                        >
                                    </div>
                                    <div v-else>
                                        {{ $t('PERMISSION_DELETED') }}
                                        {{ dateFormat(mapDateToUserTimezone(scope.row.dateRemoved)) }}
                                    </div>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </div>
            </div>
        </div>
        <modal v-if="showDeactivateAccountModal" @close="showDeactivateAccountModal = false">
            <deactivate-account @close="showDeactivateAccountModal = false" />
        </modal>
        <modal v-if="showSetNewPasswordModal" @close="showSetNewPasswordModal = false">
            <set-new-password @close="showSetNewPasswordModal = false" />
        </modal>
        <template v-if="!accountTypeIsPro">
            <h3 class="title-content">{{ $t('SHARING_DOCUMENTATION') }}</h3>
            <div class="row margin-bottom">
                <div class="col-xs-12">
                    <p>{{ $t('SHARING_DOCUMENTATION_INFO') }}</p>
                </div>
                <form @submit.prevent="addTrustedPerson" data-vv-scope="trustedPerson">
                    <div class="col-sm-3 pl-3 pr-0 sm-no-padding">
                        <input
                            class="text-input white-input-border"
                            type="text"
                            :name="$t('NAME')"
                            :placeholder="$t('NAME')"
                            v-validate="{ required: true, regex: /^.+ .+$/ }"
                            style="height: 50px"
                            v-model="trustedPerson.name"
                        />
                    </div>
                    <div class="col-sm-3 pr-0 mr-0 sm-no-padding">
                        <input
                            class="text-input white-input-border"
                            type="email"
                            :name="$t('EMAIL_ADDRESS')"
                            :placeholder="$t('EMAIL_ADDRESS')"
                            v-validate="{ required: true, regex: emailRegex }"
                            style="height: 50px"
                            v-model="trustedPerson.email"
                        />
                    </div>
                    <div class="col-sm-2 pr-0 pl-4 sm-no-padding">
                        <el-select
                            v-model="trustedPerson.dialingCode"
                            class="-white-input-border directional-number-custom"
                            style="height: 50px"
                        >
                            <el-option v-for="item in directionalNumbers" :key="item" :label="item" :value="item" />
                        </el-select>
                    </div>
                    <div class="col-sm-4 pr-4 sm-no-padding">
                        <input
                            class="text-input white-input-border"
                            type="text"
                            :name="$t('PHONE_NUMBER')"
                            :placeholder="$t('PHONE_NUMBER')"
                            v-validate="'required'"
                            style="height: 50px"
                            v-model="trustedPerson.phoneNumber"
                        />
                    </div>
                    <div class="col-sm-4 label-above">
                        <button class="app-btn app-btn--big app-btn-primary">{{ $t('ADD_PERSON') }}</button>
                    </div>
                </form>
                <div v-if="trustedPeople" class="data-details">
                    <div class="col-xs-12 mt-5">
                        <el-table :data="trustedPeople">
                            <div slot="empty">{{ $t('EMPTY_LIST') }}</div>
                            <el-table-column type="expand">
                                <template slot-scope="scope">
                                    <p class="text-break">{{ `${$t('NAME_COLON')} ${scope.row.name}` }}</p>
                                    <p class="text-break">{{ `${$t('EMAIL_ADDRESS_COLON')} ${scope.row.email}` }}</p>
                                    <p class="text-break">
                                        {{
                                            `${$t('PHONE_NUMBER_COLON')} ${
                                                scope.row.dialingCode != null ? scope.row.dialingCode : ''
                                            } ${scope.row.phoneNumber}`
                                        }}
                                    </p>
                                    <div v-if="scope.row.dateRemoved == null">
                                        <el-button
                                            @click.native.prevent="removeTrustedPerson(scope.row)"
                                            type="text"
                                            class="app-btn app-btn--small app-btn-default"
                                            >{{ $t('DELETE') }}</el-button
                                        >
                                    </div>
                                    <div v-else>
                                        {{ $t('PERMISSION_DELETED') }}
                                        {{ dateFormat(scope.row.dateRemoved) }}
                                    </div>
                                </template>
                            </el-table-column>
                            <el-table-column prop="name" :label="$t('NAME')"></el-table-column>
                            <el-table-column prop="email" :label="$t('EMAIL_ADDRESS')"></el-table-column>
                            <el-table-column v-if="!clientWidthIsSmall" :label="$t('PHONE_NUMBER')">
                                <template slot-scope="scope">
                                    {{
                                        `${scope.row.dialingCode != null ? scope.row.dialingCode : ''} ${
                                            scope.row.phoneNumber
                                        }`
                                    }}
                                </template>
                            </el-table-column>
                            <el-table-column v-if="!clientWidthIsSmall">
                                <template slot-scope="scope">
                                    <div v-if="scope.row.dateRemoved == null">
                                        <el-button
                                            @click.native.prevent="removeTrustedPerson(scope.row)"
                                            type="text"
                                            class="app-btn app-btn--small app-btn-default"
                                            >{{ $t('DELETE') }}</el-button
                                        >
                                    </div>
                                    <div v-else>
                                        {{ $t('PERMISSION_DELETED') }}
                                        {{ dateFormat(mapDateToUserTimezone(scope.row.dateRemoved)) }}
                                    </div>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </div>
            </div>
            <template v-if="user.address && user.address.country && user.address.country.code === 'IE'">
                <h3 class="title-content">{{ $t('SHARING_DOCUMENTATION_TO_DOCTOR') }}</h3>
                <div class="row margin-bottom">
                    <div class="col-xs-12">
                        <p>{{ $t('DOCTOR_OR_SOMEONE_ELSE_DATA') }}</p>
                    </div>
                    <div v-if="!showDoctorForm">
                        <div class="col-xs-12" style="width: 70%">
                            <p>{{ $t('I_DO_NOT_WANT_TO_SHARE_DOCUMENTATION') }}</p>
                        </div>
                        <button class="arrow-link" @click="showDoctorForm = true" style="padding-top: 15px">
                            {{ $t('CHANGE') }}
                            <img class="arrow-img" src="../../../assets/images/arrow-red.png" />
                        </button>
                    </div>
                    <form v-if="showDoctorForm" @submit.prevent="addTrustedDoctor">
                        <div class="col-sm-4">
                            <input
                                class="text-input white-input-border"
                                type="text"
                                :name="$t('NAME')"
                                :placeholder="$t('NAME')"
                                v-validate="{ required: true, regex: /^.+ .+$/ }"
                                v-model="trustedDoctor.name"
                            />
                        </div>
                        <div class="col-sm-4">
                            <input
                                class="text-input white-input-border"
                                type="email"
                                :name="$t('EMAIL_ADDRESS')"
                                :placeholder="$t('EMAIL_ADDRESS')"
                                v-validate="{ required: true, regex: emailRegex }"
                                v-model="trustedDoctor.email"
                            />
                        </div>
                        <div class="col-sm-4">
                            <button class="app-btn app-btn--big app-btn-primary">{{ $t('ADD_PERSON') }}</button>
                        </div>
                    </form>
                    <div v-if="trustedDoctors" class="data-details">
                        <div class="col-xs-12 mt-5">
                            <el-table :data="trustedDoctors">
                                <div slot="empty">{{ $t('EMPTY_LIST') }}</div>
                                <el-table-column type="expand">
                                    <template slot-scope="scope">
                                        <p class="text-break">{{ `${$t('NAME_COLON')} ${scope.row.name}` }}</p>
                                        <p class="text-break">
                                            {{ `${$t('EMAIL_ADDRESS_COLON')} ${scope.row.email}` }}
                                        </p>
                                        <div v-if="scope.row.dateRemoved == null">
                                            <el-button
                                                @click.native.prevent="removeTrustedDoctor(scope.row)"
                                                type="text"
                                                class="app-btn app-btn--small app-btn-default"
                                                >{{ $t('DELETE') }}</el-button
                                            >
                                        </div>
                                        <div v-else>
                                            {{ $t('PERMISSION_DELETED') }}
                                            {{ dateFormat(scope.row.dateRemoved) }}
                                        </div>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="name" :label="$t('NAME')"></el-table-column>
                                <el-table-column prop="email" :label="$t('EMAIL_ADDRESS')"></el-table-column>
                                <el-table-column v-if="!clientWidthIsSmall">
                                    <template slot-scope="scope">
                                        <div v-if="scope.row.dateRemoved == null">
                                            <el-button
                                                @click.native.prevent="removeTrustedDoctor(scope.row)"
                                                type="text"
                                                class="app-btn app-btn--small app-btn-default"
                                                >{{ $t('DELETE') }}</el-button
                                            >
                                        </div>
                                        <div v-else>
                                            {{ $t('PERMISSION_DELETED') }}
                                            {{ dateFormat(mapDateToUserTimezone(scope.row.dateRemoved)) }}
                                        </div>
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                    </div>
                </div>
            </template>
        </template>
        <section class="margin-bottom">
            <h3 class="title-content">{{ $t('PATIENT_ACCOUNT_LANG') }}</h3>
            <language-selection class="col-xs-12 mb-5 mt-2" :single="true" />
        </section>
        <h3 class="title-content">{{ $t('AGREEMENTS') }}</h3>
        <v-checkbox
            :label="$t('REGISTRY_NEWSLETTER_AGREEMENT')"
            v-model="commercialInformationAgreement"
            @change="updateUserAgreements"
            hide-details
            color="main-color"
        />
        <v-checkbox
            :label="$t('MARKETING_INFORMATION_AGREEMENT')"
            v-model="marketingAgreement"
            @change="updateUserAgreements"
            hide-details
            color="main-color"
        />
    </section>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { config, mapDateToUserTimezone } from '../../../main';
import { directionalNumbers } from './../../../countries';
import Modal from '../../modals/_modal.vue';
import SetNewPassword from '../../modals/set-new-password.vue';
import DeactivateAccount from '../../modals/deactivate-account.vue';
import languageSelection from '../../modals/main-survey/language-selection.vue';
import { clientWidthIsSmallMixin } from '../../../assets/js/mixins';

const defaultTrustedPerson = {
    name: '',
    email: '',
    dialingCode: '+48',
    number: '',
};

const defaultTrustedDoctor = {
    name: '',
    email: '',
};

export default {
    components: {
        Modal,
        SetNewPassword,
        DeactivateAccount,
        languageSelection,
    },
    mixins: [clientWidthIsSmallMixin],
    data() {
        return {
            showDeactivateAccountModal: false,
            showSetNewPasswordModal: false,
            showDoctorForm: false,
            trustedPerson: { ...defaultTrustedPerson },
            trustedDoctor: { ...defaultTrustedDoctor },
            trustedPeople: [],
            trustedDoctors: [],
            marketingAgreement: false,
            commercialInformationAgreement: false,
            directionalNumbers,
            emailRegex: config.emailRegex,
        };
    },
    computed: {
        ...mapState(['isUserLogged', 'user']),
        ...mapGetters(['accountTypeIsPro']),
        emailFontSize() {
            return this.user.email.length < 22 ? '16px' : this.user.email.length < 32 ? '14px' : '10px';
        },
    },
    methods: {
        ...mapActions(['fetchUser']),
        updateUserAgreements() {
            this.$http.get('/user/api/profile/user').then(({ data }) => {
                let agreements = data.userAgreements;
                agreements.forEach(agreement => {
                    if (agreement.agreement.label === 'MARKETING') {
                        agreement.agreed = this.marketingAgreement;
                    } else if (agreement.agreement.label === 'COMMERCIAL_INFORMATION') {
                        agreement.agreed = this.commercialInformationAgreement;
                    }
                });
                this.$http.patch('/user/api/profile/user/agreements', agreements);
            });
        },
        addTrustedPerson() {
            // console.log('user: ', this.user);
            // console.log(this.user.address, this.user.address.country, this.user.address.country.code === 'IE');

            this.$validator.validateAll('trustedPerson').then(result => {
                if (result) {
                    const names = this.trustedPerson.name.split(' ');
                    this.$http
                        .post('/user/api/settings/documentationAccessHolders', {
                            firstName: names[0],
                            lastName: names[1],
                            email: this.trustedPerson.email,
                            phoneNumber: this.trustedPerson.phoneNumber,
                            dialingCode: this.trustedPerson.dialingCode,
                        })
                        .then(() => {
                            this.trustedPerson = { ...defaultTrustedPerson };
                            this.reloadTrustedPeople();
                        });
                }
            });
        },
        removeTrustedPerson(person) {
            this.$http
                .delete(`/user/api/settings/documentationAccessHolders/${person.id}`)
                .then(this.reloadTrustedPeople);
        },
        reloadTrustedPeople() {
            this.$http.get('/user/api/settings/documentationAccessHolders').then(({ data }) => {
                data.sort((a, b) => a.id - b.id);
                data.map(i => (i.name = `${i.firstName} ${i.lastName}`));
                this.trustedPeople = data;
            });
        },
        addTrustedDoctor() {
            this.$validator.validateAll('trustedDoctor').then(result => {
                if (result) {
                    const names = this.trustedDoctor.name.split(' ');
                    this.$http
                        .post('/user/api/irish/patientAccessHolders/', {
                            firstName: names[0],
                            lastName: names[1],
                            email: this.trustedDoctor.email,
                        })
                        .then(() => {
                            this.trustedDoctor = { ...defaultTrustedDoctor };
                            this.reloadTrustedDoctors();
                        });
                }
            });
        },
        removeTrustedDoctor(doctor) {
            this.$http.delete(`/user/api/irish/patientAccessHolders/${doctor.id}`).then(this.reloadTrustedDoctors);
        },
        reloadTrustedDoctors() {
            this.$http.get('/user/api/irish/patientAccessHolders/').then(({ data }) => {
                data.sort((a, b) => a.id - b.id);
                data.map(i => (i.name = `${i.firstName} ${i.lastName}`));
                this.trustedDoctors = data;
                this.showDoctorForm = data.some(i => i.dateRemoved == null);
            });
        },
        mapDateToUserTimezone,
    },
    created() {
        this.reloadTrustedPeople();
        this.reloadTrustedDoctors();
        this.fetchUser().then(() => {
            this.marketingAgreement = this.user.userAgreements.some(
                ua => ua.agreement.label === 'MARKETING' && ua.agreed,
            );
            this.commercialInformationAgreement = this.user.userAgreements.some(
                ua => ua.agreement.label === 'COMMERCIAL_INFORMATION' && ua.agreed,
            );
        });
    },
};
</script>

<style lang="scss" scoped>
.list-circle {
    list-style-type: circle;
    list-style-position: inside;
}

.list-circle__item {
    margin: 0;
    padding: 0 0 10px;
}

.trusted-persons__list {
    padding: 0;
    margin: 20px 0 30px;
}

.trusted-persons__list__item {
    display: block;
    margin: 0;
    padding: 5px 0;
    font-size: 16px;
}

.app-input--big {
    border: 1px solid #a9a9a9;
    width: 250px;
    height: 36px;
    font-weight: 400;
    font-style: normal;
    font-size: 13px;
    color: rgb(153, 153, 153);
    padding: 0 10px;
}

.app-input--big__label {
    font-weight: 200;
}

.label-above {
    padding-top: 26px;
}

.margin-bottom {
    margin-bottom: 50px;
}

.label--small {
    font-size: 12px;
    font-weight: 100;
    margin: 0 0 20px;
}

.text-bold {
    color: #333333;
    font-size: 24px;
    font-weight: 700;
}

.arrow-img {
    width: 14px;
    margin-left: 3px;
}

@media (max-width: 768px) {
    .settings-section .text-input {
        margin-bottom: 20px;
    }

    .sm-no-padding {
        padding-left: 10px !important;
        padding-right: 10px !important;
    }

    .directional-number-custom {
        margin-bottom: 20px;
    }
}
</style>



// WEBPACK FOOTER //
// settings.vue?3197d1a8